import { useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../../../convex/_generated/api'
import { Building2, Loader2 } from 'lucide-react'

type CompanySwitcherProps = {
  brandColor?: string | null
}

export function CompanySwitcher({ brandColor }: CompanySwitcherProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const companyContext = useQuery(api.profiles.getMyCompanyContext)
  const switchActiveCompany = useMutation(api.profiles.switchActiveCompany)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState('')

  if (!companyContext || companyContext.companies.length === 0) return null

  const activeCompany = companyContext.companies.find(
    (company) => company._id === companyContext.active_company_id
  ) ?? companyContext.companies[0]

  const handleChange = async (companyId: string) => {
    if (!companyId || companyId === activeCompany?._id || switching) return
    setSwitching(true)
    setError('')
    try {
      await switchActiveCompany({ company_id: companyId as any })
      if (!location.pathname.startsWith('/company/appearance')) {
        navigate('/', { replace: true })
      }
    } catch (switchError) {
      setError(
        switchError instanceof Error
          ? switchError.message
          : 'Impossibile cambiare clinica'
      )
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary"
        style={brandColor
          ? {
              backgroundColor: 'var(--company-brand)',
              color: 'var(--company-brand-foreground)',
            }
          : undefined}
      >
        <Building2 className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="relative min-w-0">
        {companyContext.companies.length === 1 ? (
          <span className="hidden text-[11px] font-medium leading-none text-gray-500 sm:block">
            Clinica attiva
          </span>
        ) : (
          <label
            htmlFor="active-company"
            className="hidden text-[11px] font-medium leading-none text-gray-500 sm:block"
          >
            Clinica attiva
          </label>
        )}
        {companyContext.companies.length === 1 ? (
          <p className="max-w-[11rem] truncate text-sm font-medium text-gray-900 sm:mt-0.5 lg:max-w-[16rem]">
            {activeCompany?.nome}
          </p>
        ) : (
          <div className="relative sm:mt-0.5">
            <select
              id="active-company"
              value={activeCompany?._id ?? ''}
              onChange={(event) => void handleChange(event.target.value)}
              disabled={switching}
              aria-describedby={error ? 'active-company-error' : undefined}
              className="h-8 max-w-[12rem] appearance-none truncate rounded-md border border-gray-200 bg-white py-1 pl-2 pr-8 text-sm font-medium text-gray-900 outline-hidden transition-colors hover:border-gray-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:text-gray-500 sm:max-w-[16rem]"
            >
              {companyContext.companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.nome}
                </option>
              ))}
            </select>
            {switching && (
              <Loader2
                className="pointer-events-none absolute right-2 top-2 h-4 w-4 animate-spin text-primary"
                aria-hidden="true"
              />
            )}
          </div>
        )}
        {error && (
          <span
            id="active-company-error"
            className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-medium text-red-700 shadow-sm"
            role="alert"
          >
            {error}
          </span>
        )}
      </div>
    </div>
  )
}
